import { PARAM_TYPE } from '../connector.js';
import CoreUtils from '../../../utils/coreUtils.js';
import GlobalDefs from '../../../globalDefs.js';
import AssetCacher, { ASSET_TYPE } from '../assetCacher.js';
import Node from '../node.js';
import Subnet from '../subnet.js';
import { NODE_TAGS } from '../graphObject.js';
import { DEBUG_ALLOW_DIVING_INTO_PROC_ASSETS } from './../constants.js';

const HIDE_INTERNAL_INPUTS = false;

export default class NodeProceduralAsset extends Subnet
{
	constructor(pParent)
	{
		super(pParent.getGraph(), pParent);

		const pAssetProvider = this.getGraph().getAssetProvider();
		const pAssetChoices = pAssetProvider.getNodeGraphList();

		let defaultChoice = GlobalDefs.g_strNoneKey;
		this.addInput(PARAM_TYPE.DROPDOWN_PROCEDURAL, 'assetId', defaultChoice, { dropdownOptions: pAssetChoices });

		this.jsonData = null;

		this.lastLoadRequestId = '';

		this.isLoading = false;
	}
	
	getType()
	{
		return 'proceduralAsset';
	}

	getAllowResultCachingSubclass()
	{
		return true;
	}

	getIsLoading()
	{
		return this.isLoading || super.getIsLoading() || AssetCacher.getInstance().getIsNodeLoading(this);
	}

	saveToObject()
	{
		// Note that we don't actually save the contents of the graph here
		let pRes = Node.prototype.saveToObject.call(this);
		pRes['nodes'] = [];

		// We generally save the *static* value for the input; however, for the asset id,
		// we need to save the *current* value for the input, which is different if the asset id
		// value is connected to some other node. This is because we count on all procedural
		// assets being loaded before loading input values, and if the input is connected, the static
		// value will cause a different asset with different inputs being loaded first, and so
		// saved input values will get lost.
		let assetIdIdx = pRes['is'].findIndex(info => info['n'] == 'assetId');
		pRes['is'][assetIdIdx]['sv'] = this.getLastInputResult('assetId');
		return pRes;
	}

	loadFromObjectSubclass(pObj, pParent)
	{
		super.loadFromObjectSubclass(pObj, pParent);

		if(this.isLoading)
			return;

		// We really only call this for the assetId input value - otherwise, at this point it's still unloaded
		// and the load procedural asset call will fail because it has no asset id.
		this.loadInputValuesFrom(pObj['is']);
		this.loadProceduralAsset();
	}

	getReferencedAssetIds(resMapOut)
	{
		super.getReferencedAssetIds(resMapOut);

		let assetKey = this.getLastInputResult('assetId');
		if(assetKey && assetKey != GlobalDefs.g_strNoneKey && resMapOut['nodeGraphIds'].indexOf(assetKey) < 0)
			resMapOut['nodeGraphIds'].push(assetKey);
	}

	onInputChangedSubclass(strInputName)
	{
		if(strInputName === 'assetId')
			this.loadProceduralAsset();
	}

	loadProceduralAsset()
	{			
		let strSelectedKey = this.getLastInputResult('assetId');
		if(!strSelectedKey || strSelectedKey.length === 0 || strSelectedKey === GlobalDefs.g_strNoneKey)
			return;

		const pAssetProvider = this.getGraph().getAssetProvider();
		const pFileInfo = pAssetProvider.getNodeGraph(strSelectedKey);

		let strFullPath;
		if(pFileInfo)
			strFullPath = pFileInfo.file;

		let pJsonData = AssetCacher.getInstance().getCachedAsset(ASSET_TYPE.PROCEDURAL, strSelectedKey, null, undefined, this);
		if(pJsonData)
		{
			this.lastLoadRequestId = strSelectedKey;
			this.jsonData = pJsonData['subnet'];
			this.ensureContentUpdated();
			return;
		}

		if(!strFullPath || strFullPath == '')
			return;

		this.lastLoadRequestId = strSelectedKey;

		AssetCacher.getInstance().getJSONAsset(this, ASSET_TYPE.PROCEDURAL, strSelectedKey,
			() => {	this.getGraph().onStartLoadingAsset(); },
			(jsonData) => 
			{
				// The last clause is critical - it guards against a situation where
				// we start loading a large asset, then set another asset id, start loading
				// a smaller one, which completes first, only to be overwritten by the other
				// request that completes later.
				if(jsonData && this.lastLoadRequestId == strSelectedKey)
				{
					this.jsonData = jsonData['subnet'];
					this.ensureContentUpdated();
				}

				this.getGraph().onFinishLoadingAsset();
			});

		return;
	}

	addUserInput(strName, optTypeName = PARAM_TYPE.ANY, parameterConfig = {}, bIsLoading = false, bAddOutput = true, bCheckInputsOnly = false)
	{
		return super.addUserInput(strName, optTypeName, parameterConfig, bIsLoading, bAddOutput && !bIsLoading, bCheckInputsOnly);
	}

	updateConnectors()
	{
		// We need to delete old inputs and add new ones, but preserve connections.
		// The simplest way is to only delete the inputs which are not shared with the existing asset,
		// then add new ones:
		
		// Now, find the main node in the new asset:
		let pMainNode = this.getNodeTagged(NODE_TAGS.MAIN_PARAMS);
		let pNewInputs = [];
		if(pMainNode)
		{
			pNewInputs = pMainNode.getInputs();
			pMainNode.setTakeInputResultsFromParent(true);
		}

		let pOutputNode = this.getFinalOutputNode();
		let pNewOutputs = [];
		if(pOutputNode)
			pNewOutputs = pOutputNode.getOutputs();

		this.updateInputsOutputs(pNewInputs, pNewOutputs, HIDE_INTERNAL_INPUTS);
	}

	ensureContentUpdated()
	{
		if(this.isLoading)
			return;

		this.isLoading = true;
		this.pasteFromLoadObject(this.jsonData);
		this.updateConnectors();

		let pParamsNode = this.getNodeTagged(NODE_TAGS.MAIN_PARAMS);
		if(pParamsNode)
		{
			this.inputGroups = CoreUtils.clone(pParamsNode.inputGroups);

			// Unfortunately, not all parameters from the underlying asset
			// may be present as connectors on the outer node - for example,
			// we hide the inputs marked as "internal". So we need to prune
			// these names from the groups, else we crash.
			for(let groupIndex = this.inputGroups.length - 1; groupIndex >= 0; groupIndex--)
			{
				let pGroup = this.inputGroups[groupIndex];
				for(let infoIndex = pGroup.inputNames.length - 1; infoIndex >= 0; infoIndex--)
				{
					let strInputName = pGroup.inputNames[infoIndex];
					let pInput = this.getInput(strInputName);
					if(!pInput)
						this.removeInputFromGroup(strInputName);
				}

				if(this.inputGroups[groupIndex].inputNames.length === 0)
					this.deleteGroup(pGroup.name);
			}
		}
		else
			this.inputGroups = [];

		this.isLoading = false;
		this.markDirty();
	}

	getContainsOtherNodes()
	{
		if(DEBUG_ALLOW_DIVING_INTO_PROC_ASSETS)
			return true;
		return false;
	}

	dirtyUpstreamContainedNodes(strInputName)
	{
		let pMainNode = this.getNodeTagged(NODE_TAGS.MAIN_PARAMS);
		this.dirtyUpstreamContainedNodesStartingWith(pMainNode, strInputName);
	}

	getRefreshEnableStatusAfterParamChange(strInputName)
	{
		return strInputName == 'assetId';
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{
		// This takes care of the case where our asset id is changed not through the UI dropdown on the node itself,
		// but indirectly, such as through an input wired in.
		if(inputValues['assetId'] !== this.lastLoadRequestId && !this.isLoading)
			this.loadProceduralAsset();

		return super.generateSubclass(scene, inputValues, outputName, computedOutputResults);
	}

}